// Kanful ショッピングガイド — TOC スクロールスパイ
// docs/superpowers/plans/2026-04-17-shopping-guide.md Task 2.2

export function pickCurrentSection(sections, offset = 0) {
  if (!sections || sections.length === 0) return null;
  const above = sections.filter((s) => s.top <= offset);
  if (above.length === 0) return sections[0].id;
  return above[above.length - 1].id;
}

// 判定の基準線 (#104)
// 見出しには scroll-margin-top が付いており、目次から飛ぶとその分だけ下に停まる。
// 0 を基準にすると「まだ上に来ていない」と判断され、1 つ前の項目が選ばれてしまう。
// CSS の値をそのまま基準線として使う。
export function parseScrollOffset(scrollMarginTop, fallback = 0) {
  const n = Number.parseFloat(scrollMarginTop);
  return Number.isFinite(n) && n >= 0 ? n : fallback;
}

// 判定に持たせる余裕 (#104)
// 飛んだ先の見出しは基準線ぴったりで止まらず、96.4px のように小数点で
// わずかに下になることがある。そのままだと「まだ上に来ていない」と扱われ、
// 1 つ前の項目が選ばれてしまう。
export const TOC_TOLERANCE_PX = 2;

export function spyOffset(scrollMarginTop, tolerance = TOC_TOLERANCE_PX) {
  return parseScrollOffset(scrollMarginTop, 0) + tolerance;
}

// 目次を開いたままにするか (#104)
// パソコン (900px 以上) は横に出したままなので常に開く。
// スマホは開いたり閉じたりできるようにするため、こちらでは触らない。
export function shouldForceOpenToc(isWide) {
  return isWide === true;
}

// 目次の開閉を止めるか (#114)
// パソコンでは押して閉じられないようにしている。CSS の pointer-events だけでは
// キーボード操作 (Tab で移動して Enter) が通ってしまい、閉じたあと
// マウスでは開き直せなくなる。開閉そのものを止める必要がある。
export function shouldBlockTocToggle(isWide) {
  return isWide === true;
}

if (typeof window !== 'undefined') {
  const initToc = () => {
    const toc = document.querySelector('[data-toc]');
    if (!toc) return;
    const wide = window.matchMedia('(min-width: 900px)');

    // liquid 側で open を書いてあるので、スマホでは読み込み時に閉じる
    const sync = () => {
      toc.open = shouldForceOpenToc(wide.matches);
    };

    // パソコンでは閉じさせない。キーボードでも押せてしまうため、
    // 開閉そのものを止める (CSS の pointer-events だけでは足りない)
    const summary = toc.querySelector('summary');
    if (summary) {
      summary.addEventListener('click', (e) => {
        if (shouldBlockTocToggle(wide.matches)) e.preventDefault();
      });
      summary.addEventListener('keydown', (e) => {
        if (!shouldBlockTocToggle(wide.matches)) return;
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') e.preventDefault();
      });
    }

    if (typeof wide.addEventListener === 'function') {
      wide.addEventListener('change', sync);
    } else if (typeof wide.addListener === 'function') {
      // 古い Safari 向け
      wide.addListener(sync);
    }
    sync();

    // スマホでは目次から飛んだあと閉じる (開いたままだと本文が見えない)
    toc.addEventListener('click', (e) => {
      const link = e.target.closest('[data-toc-link]');
      if (link && !wide.matches) toc.open = false;
    });
  };

  const init = () => {
    initToc();
    const links = Array.from(document.querySelectorAll('[data-toc-link]'));
    if (links.length === 0) return;
    const targets = links
      .map((a) => {
        const id = a.getAttribute('href').slice(1);
        const el = document.getElementById(id);
        return el ? { id, el, link: a } : null;
      })
      .filter(Boolean);
    // 飛び先が 1 つも無いなら、現在位置を示す処理は動かさない (#114)
    if (targets.length === 0) return;

    const readOffset = () => {
      // targets が空のときに落ちないようにする (#114)。
      // 見出しの id が本文と一致しないと targets は空になる。
      if (targets.length === 0) return spyOffset('96px');
      const style = window.getComputedStyle(targets[0].el);
      return spyOffset(style.scrollMarginTop);
    };

    const update = () => {
      const rects = targets.map((t) => ({ id: t.id, top: t.el.getBoundingClientRect().top }));
      const current = pickCurrentSection(rects, readOffset());
      targets.forEach((t) => {
        if (t.id === current) t.link.setAttribute('aria-current', 'true');
        else t.link.removeAttribute('aria-current');
      });
    };

    // scroll は document だけに付けると拾えない場面があった (#104)。
    // 目次から飛んだ直後に現在位置が更新されず、読み込み時の項目が
    // 選ばれたままになる。window / hashchange / 目次のクリックでも更新する。
    // scroll は window だけに付ける。document にも付けると
    // 1 回のスクロールで 2 回動いてしまう (scroll は window まで伝わる)
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    window.addEventListener('hashchange', update);
    links.forEach((link) => {
      link.addEventListener('click', () => {
        // 飛び終わってから見ないと、移動前の位置で判定してしまう
        window.setTimeout(update, 60);
        window.setTimeout(update, 400);
      });
    });
    update();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}
